import { DesignData, ElementAddress, MediaText, Spread } from 'editor/src/store/design/types';

/**
 * Pure queries over shared text fields — no rendering or measurement, so the
 * buyer's personalizer can read them without dragging the editor's text
 * measuring code in through this module. (It arrives there anyway today, via
 * updateMediaElementOperation; see buildSharedTextValueUpdate for why the split
 * is still worth keeping.)
 */

export interface SharedTextFieldMember {
  element: MediaText;
  address: ElementAddress;
}

/**
 * Every personalisable text carrying `sharedFieldId`, in design order (spread,
 * then page, then element).
 *
 * A shared field is one value spread over several layers: the buyer types Name
 * once and it prints on the front and the back of the card. Only the string
 * travels — position, font, required flag and character cap stay per element,
 * because the back of a card sets them differently from the front.
 */
export function getSharedTextFieldMembers(
  designData: DesignData | undefined,
  sharedFieldId: string,
): SharedTextFieldMember[] {
  const members: SharedTextFieldMember[] = [];
  designData?.spreads.forEach((spread, spreadIndex) => {
    spread.pages.forEach((page, pageIndex) => {
      page.groups.media?.forEach((element, elementIndex) => {
        if (element.type === 'text' && element.extra.sharedFieldId === sharedFieldId) {
          members.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
        }
      });
    });
  });
  return members;
}

/**
 * The text uuids a spread's personalisation panel can actually put an input
 * next to — the condition group's text children, wherever they sit in the
 * option tree.
 *
 * A text layer outside that group is decoration: it prints, but the buyer is
 * never offered a field for it. The distinction matters because "personalisable"
 * cannot be inferred from the element alone.
 */
export function getPersonalizationTextUuids(spread: Spread | undefined): Set<number> {
  const uuids = new Set<number>();
  Object.values(spread?.conditionGroup?.children ?? {}).forEach((children) =>
    children.forEach((child) => {
      if (child.type === 'text') {
        uuids.add(child.id);
      }
    }),
  );
  return uuids;
}

/** Every shared-field group in the design, by id, in design order. */
function groupMembersById(spreads: Spread[] | undefined): Map<string, SharedTextFieldMember[]> {
  const byId = new Map<string, SharedTextFieldMember[]>();
  spreads?.forEach((spread, spreadIndex) => {
    spread.pages.forEach((page, pageIndex) => {
      page.groups.media?.forEach((element, elementIndex) => {
        if (element.type === 'text' && element.extra.sharedFieldId) {
          const members = byId.get(element.extra.sharedFieldId) ?? [];
          members.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
          byId.set(element.extra.sharedFieldId, members);
        }
      });
    });
  });
  return byId;
}

/**
 * Every member of a group that has no other member left.
 *
 * A group of one is not shared: the panel offers no partner to point at, so
 * the id can be neither seen nor cleared, and it would silently re-absorb the
 * next layer that joined it. Every path that can remove a member sweeps this.
 */
export function getLoneSharedFieldMembers(designData: DesignData | undefined): SharedTextFieldMember[] {
  return [...groupMembersById(designData?.spreads).values()]
    .filter((members) => members.length === 1)
    .map(([member]) => member);
}

/**
 * How many layers each group holds, by id.
 *
 * A group of one shares with nothing, so every rule that keys on "this text
 * shares its value" has to consult the count rather than the bare id — that is
 * the narrowing updateTextConditionVariantsOperation applies, and any panel
 * that gates a control on sharing has to apply the same one or it disables
 * something the operation would have allowed.
 */
export function getSharedTextFieldGroupSizes(spreads: Spread[] | undefined): Map<string, number> {
  return new Map([...groupMembersById(spreads).entries()].map(([id, members]) => [id, members.length]));
}

export interface SharedTextFieldGroup {
  sharedFieldId: string;
  members: SharedTextFieldMember[];
}

/**
 * Every group that actually shares — two members or more — in design order.
 *
 * A group of one is not one: it shares with nothing, no panel offers a partner
 * to point at, and every path that can strand a member sweeps it away. Anything
 * summarising or reasoning about "the groups in this design" wants this list
 * and not the raw ids.
 */
export function getSharedTextFieldGroups(designData: DesignData | undefined): SharedTextFieldGroup[] {
  return [...groupMembersById(designData?.spreads).entries()]
    .filter(([, members]) => members.length > 1)
    .map(([sharedFieldId, members]) => ({ sharedFieldId, members }));
}

/**
 * The ids of the groups that currently have more than one member.
 *
 * Taken before a removal and checked after it, this is what tells a sweep which
 * groups the removal itself broke — as against one that arrived already
 * stranded, which must be left alone rather than repaired inside somebody
 * else's undo entry.
 */
export function getSharedTextFieldIdsWithPartners(designData: DesignData | undefined): string[] {
  return getSharedTextFieldGroups(designData).map((group) => group.sharedFieldId);
}

/**
 * Whether the group's value actually reaches this layer.
 *
 * Two kinds of member never receive it, and every consumer has to agree on
 * both or the exclusion leaks: a rule taken from a member that does not hold
 * the value is a rule nothing can satisfy, and a write to one is a clobber.
 *
 * - `personalizationLocked`: the author has declared the layer off-limits to
 *   the buyer, so their content must not be pushed into it.
 * - `textVariants`: the layer keeps a string per picklist option, which is the
 *   author's. Writing the shared value would overwrite the live text without
 *   touching the variants map, and the next option swap would silently put it
 *   back. Reachable only from imported data, since no path will create the
 *   pairing — the same reachability the locked case is defended against.
 */
export function receivesSharedValue(element: MediaText): boolean {
  return !element.personalizationLocked && !element.extra.textVariants;
}

/**
 * The groups the buyer can actually type into: those with at least one member
 * that is unlocked, visible, and listed in its own spread's condition group.
 *
 * A member of such a group is driven by the buyer even when its OWN spread
 * offers it no field — the value arrives from wherever the field is drawn. So
 * its rules are satisfiable, and validating them is right: it is a layer the
 * buyer's typing reaches and the print file shows. A layer that belongs to no
 * driveable group and is offered no field of its own is the opposite — nothing
 * the buyer does can change it, so an error on it has nowhere to be fixed.
 */
export function getDriveableSharedTextFieldIds(designData: DesignData | undefined): Set<string> {
  const ids = new Set<string>();
  designData?.spreads.forEach((spread) => {
    const offered = getPersonalizationTextUuids(spread);
    spread.pages.forEach((page) =>
      page.groups.media?.forEach((element) => {
        // `hidden` too: a member under an inactive picklist option is in the
        // condition group but the panel never draws it, so a group whose only
        // offered member is hidden has no field anywhere — the same reason
        // lead election prefers a visible member. And the member has to
        // RECEIVE the value: one keeping a string per option neither takes the
        // group's value nor propagates its own, so it can fill nothing.
        if (
          element.type === 'text' &&
          element.extra.sharedFieldId &&
          receivesSharedValue(element) &&
          !element.hidden &&
          offered.has(element.uuid)
        ) {
          ids.add(element.extra.sharedFieldId);
        }
      }),
    );
  });
  return ids;
}

/**
 * The member that stands for the group on one spread — the first the buyer
 * could actually type into.
 *
 * The buyer's panel lists one spread at a time, so a group is shown once per
 * spread it reaches: prefilled on the second side rather than missing from it,
 * and never twice on the same one.
 *
 * "Could type into" is narrower than "is not locked", and getting that wrong
 * makes the field VANISH: everything that is not the lead renders nothing, so
 * electing a member the panel never draws — one outside the condition group, or
 * one hidden under an inactive option — leaves the buyer with no input at all.
 * Hence both filters, and the preference for a visible member over a hidden one.
 *
 * Undefined when the group reaches this spread only through members the panel
 * cannot draw; the caller treats that as "no lead to defer to" and renders.
 */
export function getSharedTextFieldLeadUuid(
  designData: DesignData | undefined,
  sharedFieldId: string,
  spreadIndex: number,
): number | undefined {
  return pickLead(getSharedTextFieldMembers(designData, sharedFieldId), designData, spreadIndex);
}

/** The lead, from a member list the caller already has. */
function pickLead(
  members: SharedTextFieldMember[],
  designData: DesignData | undefined,
  spreadIndex: number,
): number | undefined {
  const renderable = getPersonalizationTextUuids(designData?.spreads[spreadIndex]);
  const onSpread = members.filter(
    (member) =>
      member.address.spreadIndex === spreadIndex &&
      // Receives the group's value, not merely unlocked: a member keeping a
      // string per picklist option contributes no rule to the field and never
      // takes its value, so electing it would draw the field on a layer the
      // group cannot fill — and silence the member beside it that can.
      receivesSharedValue(member.element) &&
      renderable.has(member.element.uuid),
  );

  return (onSpread.find((member) => !member.element.hidden) ?? onSpread[0])?.element.uuid;
}

export interface SharedTextFieldView {
  /** False when another layer on this spread already stands for the same shared
   *  value, so the buyer is not asked for one detail twice on one screen. */
  isLead: boolean;
  maxCharCount: number | undefined;
  isMandatory: boolean;
  /** The members whose constraints bite — locked and hidden ones excluded. A
   *  caller that reports errors unions over exactly these. */
  memberUuids: number[];
}

/**
 * What a buyer-facing panel should show for one personalisable text: its own
 * settings, or, when it shares its value, the settings of the group as a whole.
 *
 * A shared group is one field, so its constraints are the union of its members'
 * — the strictest character cap holds for all of them, and one required member
 * makes the field required. Resolving it on the input the buyer can see is what
 * keeps a cap set only on the back of the card from blocking checkout from a
 * page that shows nothing wrong.
 *
 * Members that do not receive the value are left out (see receivesSharedValue),
 * and so are hidden ones: a rule taken from a layer that never holds the value,
 * or prints nothing, is one whose violation nothing can report or clear.
 *
 * The subject is held to the same test, which is the whole of the first branch
 * below. A layer carrying both `textVariants` and a `sharedFieldId` — the
 * pairing the UI refuses but imported data can arrive with — is in the group
 * only nominally: propagation skips it, so it keeps its own string. Resolving
 * the group FOR it would answer with the group's field, and hand it every
 * consequence of a membership it does not have — its own page's field
 * suppressed as a duplicate, the group's cap in place of its own, and its own
 * Required dropped while checkout still enforces it. That is the dead end this
 * feature closed for locked layers, arriving by the other door.
 *
 * Hidden is the one exclusion that is not symmetric with propagation — a hidden
 * member IS filled, so that it shows the value when its reflow row un-collapses.
 * The consequence is that the cap can tighten mid-typing as a row opens up.
 * Holding the buyer to a cap for a layer showing nothing would be the worse
 * half of that trade, and the tightening is self-correcting.
 *
 * Every live member counts, whether or not its own spread offers it a field.
 * This is only ever resolved for a text the buyer's panel is drawing, so the
 * group demonstrably has a field the buyer can type into — and the value
 * reaches every member from there, which makes each member's rules
 * satisfiable and therefore worth stating. getPersonalizationErrors validates
 * exactly this set, and the two must agree: a rule the panel shows and
 * checkout ignores asks for something nothing enforces, and one checkout
 * enforces without the panel showing it blocks a page displaying no error.
 *
 * Lives here rather than beside the personalizer's selector because the editor
 * has a buyer surface of its own — the Personalize tab — and the two must not
 * drift apart.
 */
export function resolveSharedTextField(
  designData: DesignData | undefined,
  element: MediaText | undefined,
  spreadIndex: number | undefined,
): SharedTextFieldView {
  const sharedFieldId = element?.extra.sharedFieldId;

  if (!element || !sharedFieldId || !receivesSharedValue(element)) {
    return {
      isLead: true,
      maxCharCount: element?.extra.maxCharCount,
      isMandatory: element?.extra.isMandatory === true,
      memberUuids: element ? [element.uuid] : [],
    };
  }

  // Walked once and shared with pickLead: this runs per shared field per store
  // change, and the lead needs the same list the unions below do.
  const allMembers = getSharedTextFieldMembers(designData, sharedFieldId);
  const leadUuid = spreadIndex === undefined ? element.uuid : pickLead(allMembers, designData, spreadIndex);
  const members = allMembers.filter((member) => receivesSharedValue(member.element) && !member.element.hidden);
  const caps = members
    .map((member) => member.element.extra.maxCharCount)
    .filter((cap): cap is number => cap !== undefined);

  return {
    isLead: leadUuid === undefined || leadUuid === element.uuid,
    maxCharCount: caps.length ? Math.min(...caps) : undefined,
    isMandatory: members.some((member) => member.element.extra.isMandatory === true),
    memberUuids: members.map((member) => member.element.uuid),
  };
}

/**
 * The cap each group is actually held to, by id — the strictest among the
 * members that receive its value.
 *
 * For a member of a group, this and not `extra.maxCharCount` is the limit the
 * buyer meets, so anything reporting "at the character limit" has to read it
 * here or it reports on a limit that is not the operative one. Built in one
 * pass for callers that already walk the whole design.
 *
 * A group whose every capped member is hidden or does not receive the value
 * gets no entry at all — nothing there constrains what the buyer types. So does
 * an id held by a single layer, which is not a group: nothing in this file
 * treats one as such, and its entry would only ever restate that layer's own
 * cap. Callers fall back to the layer's own cap for both, which is the right
 * answer for the author-facing warnings that use this and the only claim made
 * here.
 */
export function getSharedTextFieldCaps(designData: DesignData | undefined): Map<string, number> {
  const caps = new Map<string, number>();
  groupMembersById(designData?.spreads).forEach((members, id) => {
    if (members.length < 2) {
      return;
    }
    members.forEach(({ element }) => {
      const cap = element.extra.maxCharCount;
      if (cap === undefined || !receivesSharedValue(element) || element.hidden) {
        return;
      }
      caps.set(id, Math.min(caps.get(id) ?? cap, cap));
    });
  });
  return caps;
}

/**
 * The cap one text is actually held to, given the caps above: the group's while
 * the group's value reaches it, its own otherwise.
 *
 * Keying on the id alone is not enough. A member that keeps its own string —
 * one varying by picklist option, one locked — is in the group by name only,
 * and the group's cap is not the limit its content meets. Reading the cap
 * through here rather than off the map is what keeps the canvas, the warning
 * list and the panel saying one thing about one layer, which is the same rule
 * resolveSharedTextField applies to its subject.
 */
export function getOperativeCharCap(element: MediaText, caps: Map<string, number>): number | undefined {
  const { sharedFieldId, maxCharCount } = element.extra;

  if (!sharedFieldId || !receivesSharedValue(element)) {
    return maxCharCount;
  }

  return caps.get(sharedFieldId) ?? maxCharCount;
}

/** Whether a text edit on this element can flip its reflow row's packing. */
export function isInReflowRow(designData: DesignData | undefined, spreadIndex: number, uuid: number): boolean {
  return !!designData?.spreads[spreadIndex]?.reflowGroups?.some((group) =>
    group.rows.some((row) => row.elementUuids.includes(uuid)),
  );
}
